/**
 * Small reusable UI building blocks shared by every page.
 */
import type { Permission } from "@shared/permissions.ts";
import { type ReactNode, useState } from "react";
import { Alert, Badge, Button, Card, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router";
import { ApiError } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { Icon } from "./Icon.tsx";

export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumbs,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Array<{ label: string; to?: string }>;
}) {
  return (
    <div className="page-header mb-4">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb small mb-1">
            {breadcrumbs.map((b) => (
              <li key={b.label} className={`breadcrumb-item${b.to ? "" : " active"}`}>
                {b.to ? <Link to={b.to}>{b.label}</Link> : b.label}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div className="min-w-0">
          <h1 className="h3 mb-1 text-break">{title}</h1>
          {subtitle && <div className="text-muted">{subtitle}</div>}
        </div>
        {actions && <div className="d-flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon = "file",
  title,
  children,
  action,
}: {
  icon?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state text-center py-5 px-3">
      <div className="empty-state-icon mb-3">
        <Icon name={icon} size={32} />
      </div>
      <h2 className="h5">{title}</h2>
      {children && (
        <p className="text-muted mx-auto" style={{ maxWidth: 480 }}>
          {children}
        </p>
      )}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="d-flex align-items-center gap-2 text-muted py-4" role="status">
      <Spinner animation="border" size="sm" /> {label}
    </div>
  );
}

export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const message =
    error instanceof ApiError || error instanceof Error ? error.message : "Something went wrong";
  return (
    <Alert variant="danger" className="d-flex gap-2 align-items-start">
      <Icon name="alert" /> <span>{message}</span>
    </Alert>
  );
}

/** Loading/error wrapper for a query result. */
export function QueryState<T>({
  query,
  children,
}: {
  query: { data: T | undefined; isLoading: boolean; error: unknown };
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorAlert error={query.error} />;
  if (query.data === undefined) return null;
  return <>{children(query.data)}</>;
}

const STATUS_VARIANTS: Record<string, string> = {
  active: "success",
  current: "success",
  paid: "success",
  completed: "success",
  done: "success",
  fulfilled: "success",
  occupied: "success",
  upcoming: "info",
  scheduled: "info",
  open: "primary",
  in_progress: "primary",
  investigating: "primary",
  periodic: "primary",
  due: "warning",
  expiring: "warning",
  partial: "warning",
  waiting: "warning",
  awaiting_parts: "warning",
  reported: "warning",
  preparing: "warning",
  amend: "warning",
  for_sale: "info",
  vacant: "secondary",
  expired: "danger",
  overdue: "danger",
  urgent: "danger",
  high: "warning",
  normal: "secondary",
  low: "light",
  ended: "secondary",
  cancelled: "secondary",
  sold: "secondary",
  archived: "secondary",
  superseded: "secondary",
  not_applicable: "light",
  future: "light",
};

export function StatusBadge({ status, label }: { status: string | null | undefined; label?: string }) {
  if (!status) return null;
  const variant = STATUS_VARIANTS[status] ?? "secondary";
  return (
    <Badge
      bg={variant}
      text={variant === "light" || variant === "warning" ? "dark" : undefined}
      className="status-badge"
    >
      {label ?? status.replaceAll("_", " ")}
    </Badge>
  );
}

/** Money with semantic colour: income green, expense red, net signed. */
export function Money({
  pence,
  kind,
  whole,
  className,
}: {
  pence: number | null | undefined;
  kind?: "income" | "expense" | "net" | "outstanding";
  whole?: boolean;
  className?: string;
}) {
  const f = useFormat();
  const tone =
    kind === "income"
      ? "money-income"
      : kind === "expense"
        ? "money-expense"
        : kind === "outstanding"
          ? (pence ?? 0) > 0
            ? "money-outstanding"
            : ""
          : kind === "net"
            ? (pence ?? 0) < 0
              ? "money-expense"
              : "money-income"
            : "";
  return <span className={`money ${tone} ${className ?? ""}`.trim()}>{f.money(pence, { whole })}</span>;
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  to,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: string;
  to?: string;
  tone?: "income" | "expense" | "net" | "warning";
}) {
  const body = (
    <Card className={`stat-card h-100 ${tone ? `stat-${tone}` : ""}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div className="text-muted small text-uppercase fw-semibold">{label}</div>
          {icon && <Icon name={icon} className="text-muted" />}
        </div>
        <div className="stat-value mt-1">{value}</div>
        {hint && <div className="small text-muted mt-1">{hint}</div>}
      </Card.Body>
    </Card>
  );
  return to ? (
    <Link to={to} className="text-reset text-decoration-none d-block h-100">
      {body}
    </Link>
  ) : (
    body
  );
}

export function Section({
  title,
  actions,
  children,
  flush,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <Card className={`mb-4 ${className ?? ""}`}>
      {(title || actions) && (
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-white">
          <h2 className="h6 mb-0">{title}</h2>
          {actions && <div className="d-flex gap-2">{actions}</div>}
        </Card.Header>
      )}
      {flush ? children : <Card.Body>{children}</Card.Body>}
    </Card>
  );
}

export function DefinitionList({ items }: { items: Array<[ReactNode, ReactNode] | null | false> }) {
  return (
    <dl className="row mb-0 definition-list">
      {items.filter(Boolean).map((item, i) => {
        const [k, value] = item as [ReactNode, ReactNode];
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static list
          <div key={i} className="col-sm-6 col-lg-4 mb-3">
            <dt className="small text-muted fw-normal">{k}</dt>
            <dd className="mb-0">{value === null || value === undefined || value === "" ? "—" : value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}

/** A button that asks for confirmation before running a (possibly destructive) action. */
export function ConfirmButton({
  children,
  title,
  body,
  confirmLabel = "Delete",
  variant = "outline-danger",
  confirmVariant = "danger",
  size = "sm",
  onConfirm,
}: {
  children: ReactNode;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  variant?: string;
  confirmVariant?: string;
  /** "md" renders a normal-sized button. */
  size?: "sm" | "md" | "lg";
  onConfirm: () => Promise<unknown>;
}) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setShow(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant={variant} size={size === "md" ? undefined : size} onClick={() => setShow(true)}>
        {children}
      </Button>
      <Modal show={show} onHide={() => !busy && setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="h5">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorAlert error={error} />
          {body}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShow(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={run} disabled={busy}>
            {busy ? <Spinner size="sm" animation="border" /> : confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export function IconButtonLink({
  to,
  icon,
  children,
  variant = "primary",
}: {
  to: string;
  icon?: string;
  children: ReactNode;
  variant?: string;
}) {
  return (
    <Link to={to} className={`btn btn-${variant} d-inline-flex align-items-center gap-1`}>
      {icon && <Icon name={icon} size={16} />}
      {children}
    </Link>
  );
}

export function SubmitButton({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <Button type="submit" variant="primary" disabled={busy}>
      {busy && <Spinner size="sm" animation="border" className="me-2" />}
      {children}
    </Button>
  );
}
